/* eslint-disable max-lines */
import { useAIRecommendation } from '@hakimo-ui/hakimo/data-access';
import {
  SOPWorkflow,
  UnifiedAlarmGroup,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { openFaceComparisonTab } from '@hakimo-ui/hakimo/util';
import { ReactNode, useCallback, useState } from 'react';
import {
  isBlacklisted,
  scanFaceToUnauthorizedFace,
  toUnauthorizedFaces,
} from '../matched-faces/util';
import CameraLabels from '../sop-manager/camera-labels/CameraLabels';
import TimeRangeLabels from '../sop-manager/time-range-labels/TimeRangeLabels';
import {
  REFETCH_INTERVAL,
  RESOLVE_RECOMMENDATION,
} from './ai-recommendation/utils';
import { FaceImageProps, UnauthorizedFace } from './util';

export const UNAUTHORIZED_FACE_GUIDELINE_URL =
  'https://drive.google.com/file/d/1PAH4v5AS1RLOrzCaWtzct6h4oVzwSPsw/view?usp=sharing';

export function getZeroToleranceTextColor(
  isZeroToleranceSite?: boolean
): string {
  return isZeroToleranceSite
    ? 'text-ondark-text-1'
    : 'text-onlight-text-1 dark:text-ondark-text-1';
}

export function createSectionsContent(
  sopWorkflow: SOPWorkflow | undefined
): Record<string, { title: string; content: ReactNode }> {
  return {
    exceptions: {
      title: 'Exceptions (Do not escalate if observed)',
      content:
        sopWorkflow?.exceptions && sopWorkflow.exceptions.length > 0 ? (
          <ul className="list-disc space-y-2">
            {sopWorkflow.exceptions.map((exception, i) => (
              <li key={i} className="ml-4 list-item">
                <div className="flex flex-wrap items-center gap-2">
                  <span>{exception.text}</span>
                  <TimeRangeLabels timeRange={exception.timeRange} />
                  <CameraLabels cameras={exception.cameras} />
                </div>
              </li>
            ))}
          </ul>
        ) : null,
    },
    notes: {
      title: 'Notes',
      content:
        sopWorkflow?.notes && sopWorkflow.notes.length > 0 ? (
          <ul className="list-disc space-y-2">
            {sopWorkflow.notes.map((note, i) => (
              <li key={i} className="ml-4 list-item">
                <div className="flex flex-wrap items-center gap-2">
                  <span>{note.text}</span>
                  <TimeRangeLabels timeRange={note.timeRange} />
                  <CameraLabels cameras={note.cameras} />
                </div>
              </li>
            ))}
          </ul>
        ) : null,
    },
  };
}

export function extractUnauthorizedFaces(
  alarmGroup: UnifiedAlarmGroup
): UnauthorizedFace[] {
  const unauthorizedFaces: UnauthorizedFace[] = [];

  if (!alarmGroup?.alarms) {
    return unauthorizedFaces;
  }

  alarmGroup.alarms.forEach((alarm) => {
    // Remote guarding carries the type on the group only; Scan sets it per
    // alarm. Prefer the alarm's own type where there is one.
    const alarmType = alarm.alarm_type ?? alarmGroup.locationAlarmType;
    if (alarm.media?.type === 'frames' && alarm.media.frames) {
      alarm.media.frames.forEach((frame) => {
        frame.matched_faces?.forEach((face) => {
          if (isBlacklisted(face.profile_type)) {
            unauthorizedFaces.push(
              scanFaceToUnauthorizedFace(face, frame.timestamp, {
                cameraName: alarm.cameraName,
                frameUrl: frame.url,
                alarmType,
              })
            );
          }
        });
      });
    } else if (
      alarm.media?.type === 'video' &&
      alarm.media.video?.matchedFaces
    ) {
      unauthorizedFaces.push(
        ...toUnauthorizedFaces(alarm.media.video.matchedFaces, {
          cameraName: alarm.cameraName,
          videoStartTime: alarm.media.video.startTime,
          alarmType,
        })
      );
    }
  });

  return unauthorizedFaces;
}

export function useShouldHideAIRecommendation(
  locationAlarmId: string,
  product: UnifiedMonitoringProduct,
  unauthorizedFaces?: UnauthorizedFace[]
): boolean {
  const { data: aiRecommendationData } = useAIRecommendation(
    locationAlarmId,
    product,
    REFETCH_INTERVAL
  );
  const aiOperatorRep = aiRecommendationData
    ? aiRecommendationData[0]
    : undefined;
  const isAIRecommendationResolve =
    aiOperatorRep?.recommendations === RESOLVE_RECOMMENDATION;
  const hasUnauthorizedFaces =
    unauthorizedFaces && unauthorizedFaces.length > 0;

  return !!(isAIRecommendationResolve && hasUnauthorizedFaces);
}

export function FaceImage(props: FaceImageProps) {
  const {
    imagePath,
    profileName,
    employeeId,
    gender,
    timestamp,
    cameraName,
    alarmType,
    frameUrl,
    faceBox,
    fullBodyImagePath,
    personBox,
  } = props;
  const [hasError, setHasError] = useState(false);

  const handleImageError = useCallback(() => {
    setHasError(true);
  }, []);

  const handleClick = useCallback(() => {
    openFaceComparisonTab({
      profileName,
      employeeId,
      gender,
      timestamp,
      cameraName,
      alarmType,
      referencePrimaryUrl: imagePath,
      referenceFullBodyUrl: fullBodyImagePath,
      frameUrl,
      faceBox,
      personBox,
    });
  }, [
    imagePath,
    profileName,
    employeeId,
    gender,
    timestamp,
    cameraName,
    alarmType,
    frameUrl,
    faceBox,
    fullBodyImagePath,
    personBox,
  ]);

  if (hasError) {
    return null;
  }

  return (
    <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full">
      <button
        type="button"
        onClick={handleClick}
        className="block h-full w-full cursor-pointer"
        title={profileName}
      >
        <img
          src={imagePath}
          alt={profileName}
          className="h-full w-full object-cover"
          onError={handleImageError}
        />
      </button>
    </div>
  );
}
